import { Router } from '@angular/router'
import { JwtHelperService } from '@auth0/angular-jwt'
import { Navigate } from '@ngxs/router-plugin'
import { Action, Selector, State, StateContext, Store } from '@ngxs/store'
import {
  CustomerInfosFragment,
  ForgotPasswordGQL,
  LoginGQL,
  MeGQL,
  UpdatePasswordGQL,
  UpdatePasswordWithTokenGQL,
} from '../graphql'
import { UserRole } from '@wm-front/shared/enums'
import { Apollo } from 'apollo-angular'
import { DecodedToken } from '@wm-front/shared/interfaces'
import { StateResetAll } from 'ngxs-reset-plugin'
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import {
  FetchUserInfos,
  ForgotPassword,
  Login,
  LoginFromStorage,
  Logout,
  SetToken,
  UpdatePassword as UpdatePasswordWithOldPassword,
  UpdatePasswordWithToken,
} from './auth.actions'
import { CreatePassword, UpdatePassword } from './interfaces'
import { CreatePasswordError, UpdatePasswordError } from './enums'
import { Injectable, DOCUMENT, inject } from '@angular/core'
import { CountryCode } from 'libphonenumber-js/min'
import { FeatureFlagService } from '@wm-front/core-tools'
import { CubejsService, SegmentService, SocketService } from '@wm-front/core'
import { getBackendErrorCode } from '@wm-front/shared/utils'
import { Environment, ENVIRONMENT } from '@wm-front/shared/environment'
import { L10nService } from '@wm-front/admin/l10n'
import { toSignal } from '@angular/core/rxjs-interop'
import { ClerkService } from '@wm-front/clerk'

export interface AuthStateModel {
  accessToken: string
  decodedToken: DecodedToken | null
  hasMaximize: boolean
  userInfos?: CustomerInfosFragment
  error: CreatePasswordError | null
  loading: boolean
  createPassword: CreatePassword
  updatePassword: UpdatePassword
  mode: 'legacy' | 'clerk'
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    accessToken: '',
    mode: 'legacy',
    decodedToken: {
      id: 0,
      email: '',
      currencyIsoCode: 'EUR',
      iat: 0,
      exp: 0,
      payload: {
        customerId: 0,
        role: UserRole.Customer,
        ghosted: false,
      },
    },
    updatePassword: {
      error: null,
      loading: false,
    },
    error: null,
    loading: false,
    hasMaximize: false,
    createPassword: {
      loading: false,
      error: null,
    },
  },
})
@Injectable()
export class AuthState {
  protected _router = inject(Router)

  protected _loginGQL = inject(LoginGQL)

  protected _MeGQL = inject(MeGQL)

  protected _forgotPasswordGQL = inject(ForgotPasswordGQL)

  protected _updatePasswordWithTokenGQL = inject(UpdatePasswordWithTokenGQL)

  protected _updatePasswordGQL = inject(UpdatePasswordGQL)

  protected _store = inject(Store)

  protected _apollo = inject(Apollo)

  protected _segment = inject(SegmentService)

  protected _cubejs = inject(CubejsService)

  protected _socket = inject(SocketService)

  protected _featureFlagService = inject(FeatureFlagService)

  protected _clerk = inject(ClerkService)

  protected shouldNotBeAnonymous = toSignal(
    this._featureFlagService.watch('deactivate-anonymity-on-the-client', false)
  )

  protected _l10nService = inject(L10nService)

  protected _document = inject<Document>(DOCUMENT)

  protected env = inject<Environment>(ENVIRONMENT)

  /** The name of the token as stored in the local storage */
  private tokenName = 'wm-client-token'

  /** The jwt helper service provided by the @auth/angular-jwt package */
  private jwtHelper = new JwtHelperService()

  /** The window object */
  private window: Window

  constructor() {
    const _document = this._document

    this.window = _document.defaultView as Window
  }

  /** Select Auth access token
   * @param { AuthStateModel } state the state model
   * @return { string }
   */
  @Selector()
  public static accessToken(state: AuthStateModel): string {
    return state.accessToken
  }

  /** Select Auth decoded token
   * @param { AuthStateModel } state the state model
   * @return { DecodedToken }
   */
  @Selector()
  public static decodedToken(state: AuthStateModel): DecodedToken | null {
    return state.decodedToken
  }

  /** Select user id from the current logged in user
   * @param { AuthStateModel } state the state model
   * @return { number | string | undefined }
   */
  @Selector()
  public static customerId(state: AuthStateModel): number | string | undefined {
    return state.decodedToken?.payload.customerId
  }

  /** Select account ids from the current logged in user
   * @param { AuthStateModel } state the state model
   * @return { number[] | undefined }
   */
  @Selector()
  public static accountIds(
    state: AuthStateModel
  ): number[] | string[] | undefined {
    return state.decodedToken?.payload.accountIds
  }

  /**  Selects whether or not the user has connected
   * @param { AuthStateModel } state the state model
   * @return { boolean }
   */
  @Selector()
  public static isAuthenticated(state: AuthStateModel): boolean {
    const jwtHelper = new JwtHelperService()

    return !jwtHelper.isTokenExpired(state.accessToken)
  }

  /** Selects whether or not the user has maximize
   * @param { AuthStateModel } state the state model
   * @return { boolean }
   */
  @Selector()
  public static hasMaximize(state: AuthStateModel): boolean {
    return state.hasMaximize
  }

  /** Selects whether or not the user has maximize
   * @param { AuthStateModel } state the state model
   * @return { boolean }
   */
  @Selector()
  public static hasNoMaximize(state: AuthStateModel): boolean {
    return !state.hasMaximize
  }

  /** Select currencyIsoCode from the current logged in user
   * @param { AuthStateModel } state the state model
   * @return { string }
   */
  @Selector()
  public static currencyIsoCode(state: AuthStateModel): string {
    return state?.userInfos?.currencyIsoCode || 'EUR'
  }

  /** Select email from the current logged in user
   * @param { AuthStateModel } state the state model
   * @return { string }
   */
  @Selector()
  public static email(state: AuthStateModel): string {
    return state?.userInfos?.email || ''
  }

  /** Select fullName from the current logged in user
   * @param { AuthStateModel } state the state model
   * @return { string }
   */
  @Selector()
  public static fullName(state: AuthStateModel): string {
    return state.userInfos
      ? `${state.userInfos.firstName} ${state.userInfos.lastName}`
      : ''
  }

  /** Select the countryCode from the current logged in user
   * @param { AuthStateModel } state the state model
   * @return { CountryCode }
   */
  @Selector()
  public static countryCode(state: AuthStateModel): CountryCode {
    return state.userInfos?.countryCode as CountryCode
  }

  /** Select whether or not the login has failed
   * @param { AuthStateModel } state the state model
   * @return { boolean }
   */
  @Selector()
  public static errorLogin(state: AuthStateModel): boolean {
    return !!state.error
  }

  /**  Selects whether or not the create password has an error
   * @param { AuthStateModel } state the state model
   * @return { Error | null }
   */
  @Selector()
  public static isCreatePasswordError(state: AuthStateModel): string | null {
    return state.createPassword.error
  }

  /**  Selects whether or not the create password is loading
   * @param {AuthStateModel} state the state model
   * @return {boolean}
   */
  @Selector()
  public static isCreatePasswordLoading(state: AuthStateModel): boolean {
    return state.createPassword.loading
  }

  /**  Selects whether or not the update password has an error
   * @param { AuthStateModel } state the state model
   * @return { string | null }
   */
  @Selector()
  public static isUpdatePasswordError(state: AuthStateModel): string | null {
    return state.updatePassword.error
  }

  /**  Selects whether or not the update password is loading
   * @param {AuthStateModel} state the state model
   * @return {boolean}
   */
  @Selector()
  public static isUpdatePasswordLoading(state: AuthStateModel): boolean {
    return state.updatePassword.loading
  }

  /** Set the login
   * @param { StateContext<AuthStateModel> } ctx the state context
   * @param { Login } payload the tab value used to patch the state
   * @return { Promise<AuthStateModel | undefined> }
   */
  @Action(Login)
  public login(
    ctx: StateContext<AuthStateModel>,
    { payload }: Login
  ): Observable<AuthStateModel | undefined> {
    return this._loginGQL
      .mutate({ email: payload.email, password: payload.password })
      .pipe(
        map((res) => {
          if (res.data) {
            const accessToken = res.data.login.access_token

            this.setTokenInStorage(accessToken)
            const decodedToken = this.jwtHelper.decodeToken(
              accessToken
            ) as AuthStateModel['decodedToken']

            this._cubejs.init(
              accessToken,
              [
                {
                  version: 'cubi',
                  apiUrl: this.env.cubejsV2ApiUrl,
                },
              ],
              decodedToken?.payload.preferedTimezone
            )

            if (decodedToken?.payload.countryCode) {
              this._l10nService.territory$.set(decodedToken.payload.countryCode)
            }

            // this._socket.connect(environment.socketUrl, accessToken)

            this._store.dispatch(new Navigate(['/platform/dashboard']))

            return ctx.patchState({
              accessToken: accessToken,
              decodedToken: this.jwtHelper.decodeToken(
                accessToken
              ) as AuthStateModel['decodedToken'],
              error: null,
            })
          } else {
            return undefined
          }
        }),
        catchError((e) => {
          ctx.patchState({
            error: e,
          })

          return of(e)
        })
      )
  }

  /** Set the token in the store and storage (used by Clerk session bridge)
   * @param { StateContext<AuthStateModel> } ctx the state context
   * @param { SetToken } action the token and mode to set
   */
  @Action(SetToken)
  public setToken(
    ctx: StateContext<AuthStateModel>,
    { token, mode }: SetToken
  ): void {
    if (token) {
      this.setTokenInStorage(token)
      const decodedToken = this.jwtHelper.decodeToken(
        token
      ) as AuthStateModel['decodedToken']

      // Mirror the legacy Login side effects: without this, a pure-Clerk
      // sign-in (empty localStorage, so LoginFromStorage did nothing) never
      // initializes CubeJS and every dashboard widget crashes on
      // `undefined.rawData()`. Re-initing on each refresh of the bridged
      // token (every ~25s) also keeps the token embedded in the CubeJS
      // client fresh, where the legacy paths handed it a 60s snapshot.
      this._cubejs.init(
        token,
        [
          {
            version: 'cubi',
            apiUrl: this.env.cubejsV2ApiUrl,
          },
        ],
        decodedToken?.payload.preferedTimezone
      )

      if (decodedToken?.payload.countryCode) {
        this._l10nService.territory$.set(decodedToken.payload.countryCode)
      }

      ctx.patchState({
        accessToken: token,
        mode,
        decodedToken,
        error: null,
      })
    }
  }

  /** Set the login from localStorage
   * @param { StateContext<AuthStateModel> } ctx the state context
   * @param { Login } payload the tab value used to patch the state
   * @return { AuthStateModel | void }
   */
  @Action(LoginFromStorage)
  public loginFromStorage(ctx: StateContext<AuthStateModel>): void {
    const mode = this.window.localStorage.getItem('auth-mode') || 'legacy'
    if (mode === 'clerk') return // Clerk manages its own session

    const tokenStorage = this.window.localStorage.getItem(this.tokenName)

    if (tokenStorage) {
      const jwtHelper = new JwtHelperService()
      const isTokenExpired = jwtHelper.isTokenExpired(tokenStorage)

      const decodedToken = this.jwtHelper.decodeToken(
        tokenStorage
      ) as AuthStateModel['decodedToken']

      // Fix waiting for invalid token
      if (!decodedToken?.payload?.accountIds) {
        this.logout(ctx)
          .then()
          .catch((e) => {
            console.error(e)
          })
        return
      }

      if (!isTokenExpired) {
        this._cubejs.init(
          tokenStorage,
          [
            {
              version: 'cubi',
              apiUrl: this.env.cubejsV2ApiUrl,
            },
          ],
          decodedToken.payload.preferedTimezone
        )

        if (decodedToken.payload.countryCode) {
          this._l10nService.territory$.set(decodedToken.payload.countryCode)
        }

        ctx.patchState({
          accessToken: tokenStorage,
          decodedToken,
        })
      }
    }
  }

  /** Reset the login from localStorage and navigate to the login screen
   * @param { StateContext<AuthStateModel> } ctx the state context
   * @return { AuthStateModel }
   */
  @Action(Logout)
  public async logout(ctx: StateContext<AuthStateModel>): Promise<void> {
    this.removeTokenInStorage()
    if (this.env.clerk) {
      this._clerk.signOut()
    }
    this._store.dispatch(new StateResetAll())
    await this._apollo.client.resetStore()

    ctx.patchState({ accessToken: '' })
    this._store.dispatch(new Navigate(['/auth/login']))
  }

  /** Sends the email of reset password from the forgot password page
   * @param { StateContext<AuthStateModel> } ctx the state context
   * @param { ForgotPassword } payload the email entered in the form on the forgot password page
   * @return { Observable<void> }
   */
  @Action(ForgotPassword)
  public forgotPassword(
    ctx: StateContext<AuthStateModel>,
    { payload }: ForgotPassword
  ): Observable<void> {
    return this._forgotPasswordGQL.mutate(payload).pipe(
      map(() => {
        this._store.dispatch(new Navigate(['/auth/email-sent']))
      }),
      catchError((e) => {
        return of(e)
      })
    )
  }

  /** Set user info
   * @param { StateContext<AuthStateModel> } ctx the state context
   * @return { Observable<AuthStateModel> }
   */
  @Action(FetchUserInfos)
  public fetchUserInfos(
    ctx: StateContext<AuthStateModel>
  ): Observable<Promise<AuthStateModel>> {
    return this._MeGQL.fetch().pipe(
      map(async (res) => {
        const user = res.data.me as CustomerInfosFragment
        const ghosted = ctx.getState().decodedToken?.payload.ghosted

        const intercom = {
          integrations: { Intercom: { user_hash: user.intercomHashId } },
        }

        const hasMaximize = await firstValueFrom(
          combineLatest([
            this._featureFlagService.watch('client-platform-maximize', false),
            of(user.hasMaximize as boolean),
          ]).pipe(map(([ld, me]) => ld || me))
        )

        this._segment.identify(
          user.id,
          { ...user, ghosted },
          intercom,
          this.shouldNotBeAnonymous()
        )

        ctx.patchState({
          userInfos: user,
          hasMaximize,
        })
        return ctx.getState()
      }),
      catchError(async (err) => {
        if (
          err
            .toString()
            .match(
              '^ApolloError: Response not successful: Received status code 40*'
            )
        ) {
          await this.logout(ctx)
        }
        throw new Error(err)
      })
    )
  }

  /** Update the password with token
   * @param { StateContext<AuthStateModel> } ctx the state context
   * @param { UpdatePasswordWithToken } payload the token and the new password
   */
  @Action(UpdatePasswordWithToken)
  public updatePasswordWithToken(
    ctx: StateContext<AuthStateModel>,
    { payload }: UpdatePasswordWithToken
  ): Observable<void> {
    ctx.patchState({ createPassword: { loading: true, error: null } })

    return this._updatePasswordWithTokenGQL.mutate(payload).pipe(
      map((res) => {
        const data = res ? res.data : false
        let isError: CreatePasswordError | null = null
        if (data && data.updatePasswordWithToken) {
          this._store.dispatch(new Navigate(['/auth/password-created']))
        } else {
          isError = CreatePasswordError.UnknownError
        }

        ctx.patchState({ createPassword: { error: isError, loading: false } })
      }),
      catchError((err) => {
        const errorCode = getBackendErrorCode<CreatePasswordError>(err)

        ctx.patchState({ createPassword: { error: errorCode, loading: false } })

        return of(err)
      })
    )
  }

  /** Update the password
   * @param { StateContext<AuthStateModel> } ctx the state context
   * @param { UpdatePasswordWithOldPassword } payload the old password, the new password and the callback on finish
   */
  @Action(UpdatePasswordWithOldPassword)
  public updatePassword(
    ctx: StateContext<AuthStateModel>,
    { payload }: UpdatePasswordWithOldPassword
  ): Observable<void> {
    ctx.patchState({
      updatePassword: {
        loading: true,
        error: null,
      },
    })

    return this._updatePasswordGQL.mutate(payload).pipe(
      map(() => {
        ctx.patchState({ updatePassword: { error: null, loading: false } })

        if (payload.onFinish) {
          payload.onFinish().catch((err) => {
            throw err
          })
        }
      }),
      catchError((err) => {
        const errorCode = getBackendErrorCode<UpdatePasswordError>(err)

        ctx.patchState({ updatePassword: { error: errorCode, loading: false } })

        return of(err)
      })
    )
  }

  /** Set token in local storage
   * @param { string } accessToken
   * @return { void }
   */
  private setTokenInStorage(accessToken: string): void {
    this.window.localStorage.setItem(this.tokenName, accessToken)
  }

  /** Remove token in local storage
   * @return { void }
   */
  private removeTokenInStorage(): void {
    this.window.localStorage.removeItem(this.tokenName)
  }
}
